import React, { forwardRef, ElementType, ReactNode, useState, useCallback } from 'react';
import { Box, BoxProps, SliderProps, Typography, TypographyProps, FilledInput, FilledInputProps } from '@mui/material';
import { flowSlider } from '@/src/materialui/slider/slider.theme';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';

export interface SliderValuesProps extends BoxProps<ElementType> {
  /**
   * The value of the slider
   */
  value: SliderProps['value'] | false;
  /**
   * Properties applied to value wrapper
   */
  valueProps?: TypographyProps<ElementType>;
  /**
   * Label show above value
   */
  label?: ReactNode;
  /**
   * Properties applied to label wrapper
   */
  labelProps?: TypographyProps<ElementType>;
  /**
   * The format function the value label's value
   */
  valueLabelFormat?: SliderProps['valueLabelFormat'];
  /**
   * Provide this function to allow changing the slider value with inputs
   */
  setValue?: (newValue: unknown) => void;
  /**
   * Disable editing
   */
  disabled?: boolean;
  /**
   * The minimum allowed value of the slider
   */
  min?: SliderProps['min'];
  /**
   * Text shown above min selected value
   */
  minLabel?: ReactNode;
  /**
   * Properties applied to label wrapper
   */
  minLabelProps?: TypographyProps<ElementType>;
  /**
   * The maximum allowed value of the slider
   */
  max?: SliderProps['max'];
  /**
   * Text shown above max selected value
   */
  maxLabel?: ReactNode;
  /**
   * Properties applied to label wrapper
   */
  maxLabelProps?: TypographyProps<ElementType>;
  /**
   * The granularity with which the slider can step through values
   */
  step?: SliderProps['step'];
  /**
   * Properties applied to the root wrapper of each SliderValue
   */
  sliderValueProps?: BoxProps<ElementType>;
  /**
   * Properties applied to SliderValue input field
   */
  inputProps?: FilledInputProps;
}

/**
 * Wrapper that render one or two SliderValue components based on the Slider type
 */
export const SliderValues: React.FC<SliderValuesProps> = forwardRef(
  (
    {
      sx,
      children,
      value,
      valueProps,
      label,
      labelProps,
      valueLabelFormat,
      setValue,
      disabled,
      min = flowSlider.defaultProps.min,
      minLabel,
      minLabelProps,
      max = flowSlider.defaultProps.max,
      maxLabel,
      maxLabelProps,
      step = flowSlider.defaultProps.step,
      sliderValueProps: rawSliderValueProps,
      inputProps,
      ...rest
    },
    ref,
  ) => {
    const sliderValueProps = {
      disabled,
      min,
      max,
      step,
      valueLabelFormat,
      valueProps,
      inputProps,
      ...rawSliderValueProps,
    };

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 1,
          ...sx,
        }}
        {...rest}
      >
        {Array.isArray(value) || (value === false && (minLabel || label) && maxLabel) ? (
          <>
            <SliderValue
              value={value !== false && value[0]}
              setValue={setValue ? (newValue) => setValue([newValue, value[1]]) : undefined}
              label={minLabel || label}
              labelProps={minLabelProps || labelProps}
              {...sliderValueProps}
              max={value[1]}
            />
            <SliderValue
              value={value !== false && value[1]}
              setValue={setValue ? (newValue) => setValue([value[0], newValue]) : undefined}
              label={maxLabel}
              labelProps={maxLabelProps}
              {...sliderValueProps}
              min={value[0]}
              sx={{ textAlign: 'right', ...sliderValueProps?.sx }}
            />
          </>
        ) : (
          <SliderValue value={value} setValue={setValue} label={label} labelProps={labelProps} {...sliderValueProps} />
        )}
        {children}
      </Box>
    );
  },
);

export interface SliderValueProps extends Omit<
  SliderValuesProps,
  'value' | 'minLabel' | 'minLabelProps' | 'maxLabel' | 'maxLabelProps'
> {
  /**
   * The single value of the slider
   */
  value: number | false;
  /**
   * The single setValue for the slider value
   */
  setValue?: (newValue: number) => void;
}

/**
 * Display single value of slider, allows editing with input
 */
export const SliderValue: React.FC<SliderValueProps> = forwardRef(
  (
    {
      value,
      valueProps,
      label,
      labelProps,
      valueLabelFormat,
      setValue,
      min,
      max,
      step,
      inputProps,
      disabled,
      sx,
      ...rest
    },
    ref,
  ) => {
    const { sx: labelSx, ...restLabel } = labelProps ?? {};
    const { sx: valueSx, ...restValue } = valueProps ?? {};
    const { sx: inputSx, ...restInput } = inputProps ?? {};
    const [editing, setEditing] = useState(false);
    const editValue = useCallback(
      (inputValue: string) => {
        const newValue = +(inputValue || 0);
        if (newValue < min) {
          setValue(min);
        } else if (newValue > max) {
          setValue(max);
        } else {
          setValue(newValue);
        }
      },
      [min, max, setValue],
    );
    const formattedValue = valueLabelFormat ? valueLabelFormat(value) : value;

    return (
      <Box
        ref={ref}
        sx={{ minWidth: '82px', ...((!label || value === false) && { pt: 0.5 }), ...sx }}
        onClick={setValue && !disabled ? () => setEditing(true) : undefined}
        {...rest}
      >
        {label && (
          <Typography
            {...(value === false && {
              variant: 'disclaimer1',
            })}
            sx={{
              display: 'block',
              ...(value === false
                ? {
                    color: (theme) => (theme.palette.mode === 'dark' ? 'onSurface.os200' : 'onSurface.os700'),
                  }
                : {
                    // os600 instead of Figma's os500 to pass A11y contrast
                    color: (theme) => (theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os600'),
                    fontSize: { xs: '12px' },
                    lineHeight: { xs: '12px' },
                    fontWeight: { xs: '400' },
                    py: 0.5,
                    px: 1,
                  }),
              ...labelSx,
            }}
            {...restLabel}
          >
            {label}
          </Typography>
        )}
        {editing && !disabled ? (
          <FilledInput
            autoFocus
            onBlur={() => setEditing(false)}
            value={value}
            onChange={(e) => editValue(e.target.value)}
            onKeyUp={() => {}}
            inputProps={{
              type: 'number',
              step: step,
              min: min,
              ['aria-valuemin']: min,
              max: max,
              ['aria-valuemax']: max,
              ['aria-valuenow']: formattedValue,
            }}
            disableUnderline
            sx={(theme) => ({
              display: 'block',
              borderRadius: '4px',
              py: '1px', // pixel perfect matching design
              px: 1,
              border: 1,
              borderColor: 'onSurface.os500',
              '&&, &:hover': {
                backgroundColor: 'background.primary',
              },
              // input itself
              '& .MuiInputBase-input': {
                fieldSizing: 'content',
                textAlign: 'inherit',
                p: 0,
                // matching typography style with value
                ...theme.typography[restValue?.variant ?? 'disclaimer1'],
              },
              ...getFocusRingStyle(theme, false, 1, '&.Mui-focused'),
              '&&:before': {
                border: 'none',
              },
              ...inputSx,
            })}
            {...restInput}
          />
        ) : (
          value !== false && (
            <Typography
              variant="disclaimer1"
              sx={{
                display: 'block',
                px: 1,
                whiteSpace: 'nowrap',
                color: (theme) => (theme.palette.mode === 'dark' ? 'onSurface.os200' : 'onSurface.os700'),
                // editable
                ...(setValue && {
                  px: 1,
                  py: '1px',
                  border: 1,
                  borderColor: 'onSurface.os500',
                  borderRadius: '4px',
                }),
                ...valueSx,
              }}
              {...(setValue && {
                // allow focus with keyboard navigation
                tabIndex: 0,
                onFocus: () => setEditing(true),
              })}
              {...restValue}
            >
              {formattedValue}
            </Typography>
          )
        )}
      </Box>
    );
  },
);
